import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { ThemeProvider } from 'styled-components';
import { isEmailVerifiedSignalEnabled, isSurveysRouteEnabled } from '@helpcenter/feature-flags';
import { lightenColor, readableTextColor } from '@helpcenter/styles/src/colors';
import { GlobalStyled } from '@helpcenter/styles/src/global';
import { createMemoryHistory } from 'history';
import { di } from 'react-magnetic-di';
import type { BrowserHistory, Route } from 'react-resource-router';
import { RouteComponent, Router } from 'react-resource-router';
import { consentFormRoute } from 'routes/consent-form';
import { embedKBArticleRoute, getEmbedCreateRequestRoute } from 'routes/embed-smartlinks';
import {
	helpcenterEditRoute,
	helpcenterManageProjectsRoute,
	helpcenterManageCustomPagesRoute,
} from 'routes/help-center-settings';
import { viewAllSearchResultsRoute } from 'routes/kb';

import { userVerifyEmailRoute } from 'routes/verify-email';
import { getHelpCenterTitleColor, getThemeColor } from 'state/selectors/help-center';
import { UFOWatcher } from 'utils/ufo-watcher';
import {
	getAnonymousRequestSuccessRouteWithHubShell,
	getCheckEmailRouteWithHubShell,
	getCompleteSignupRouteWithHubShell,
	getHubCustompageRoute,
	getHubHomepageRoute,
	getHubPortalPageRoute,
	getHubViewPortalArticleRoute,
	getHubUserProfileRouteWithHubShell,
	getHubViewArticleRoute,
	getHubViewCrossSiteArticleRoute,
	getHubViewCrossSitePortalArticleRoute,
	getJsmApprovalsRouteWithHubShell,
	getJsmRequestApprovalRouteWithHubShell,
	getJsmRequestDetailsRouteWithHubShell,
	getJsmRequestsListRouteWithHubShell,
	getLoginRouteWithHubShell,
	getNotFoundRouteWithHubShell,
	getPortalAccessForbiddenRouteWithHubShell,
	getPortalCompleteSignupRouteWithHubShell,
	getPortalInvitedSignupRouteWithHubShell,
	getPortalLoginRouteWithHubShell,
	getPortalResetPasswordRouteWithHubShell,
	getPortalSignupRouteWithHubShell,
	getRequestCreateRouteWithHubShell,
	getRequestFeedbackRouteWithHubShell,
	getResetPasswordRouteWithHubShell,
	getSignupRouteWithHubShell,
	getUnsubscribeRouteWithHubShell,
	getHubViewKbRoute,
	getSurveyRouteWithHubShell,
} from 'view/hub-routes';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { history, getBaseName } from '@atlassian/help-center-common-util/history';
import { RovoWidgetRenderer } from '../../rovo-widget-renderer';
import { resourcesPlugin } from '../resource-plugin';

export const getRoutes = (): Route[] => {
	return [
		getHubHomepageRoute(),
		getHubCustompageRoute(),
		...(FeatureGates.checkGate('portal-pages-in-hub') ? [getHubPortalPageRoute()] : []),

		/**
		 * Account Management Pages
		 */
		getLoginRouteWithHubShell(),
		getPortalLoginRouteWithHubShell(),
		getResetPasswordRouteWithHubShell(),
		getPortalResetPasswordRouteWithHubShell(),
		getSignupRouteWithHubShell(),
		getPortalSignupRouteWithHubShell(),
		getCompleteSignupRouteWithHubShell(),
		getPortalCompleteSignupRouteWithHubShell(),
		getPortalInvitedSignupRouteWithHubShell(),
		getCheckEmailRouteWithHubShell(),
		getUnsubscribeRouteWithHubShell(),
		getHubUserProfileRouteWithHubShell(),
		getPortalAccessForbiddenRouteWithHubShell(),
		consentFormRoute /* TODO: The consent form route needs the Hub Shell */,
		...(isEmailVerifiedSignalEnabled() ? [userVerifyEmailRoute] : []),

		/**
		 * Request Management Pages
		 */
		getRequestCreateRouteWithHubShell(),
		getAnonymousRequestSuccessRouteWithHubShell(),
		getRequestFeedbackRouteWithHubShell(),
		getJsmRequestDetailsRouteWithHubShell(),

		getJsmRequestsListRouteWithHubShell(),
		getJsmApprovalsRouteWithHubShell(),
		getJsmRequestApprovalRouteWithHubShell(),
		...(isSurveysRouteEnabled() ? [getSurveyRouteWithHubShell()] : []),
		/** TODO: Article Pages */
		getHubViewKbRoute(),
		getHubViewArticleRoute(),
		getHubViewCrossSiteArticleRoute(),
		getHubViewPortalArticleRoute(),
		getHubViewCrossSitePortalArticleRoute(),

		/**
		 * Search related pages
		 */
		viewAllSearchResultsRoute,

		/**
		 * Hub Management pages
		 */
		helpcenterEditRoute,
		helpcenterManageProjectsRoute,
		helpcenterManageCustomPagesRoute,

		/**
		 * EmbeddablePages
		 */
		getEmbedCreateRequestRoute(),
		embedKBArticleRoute,

		/**
		 * Not found route
		 */
		getNotFoundRouteWithHubShell(),
	];
};

export interface Props {
	themeColor: string;
	titleColor: string;
}

export const HubResourceRouter = (props: Props) => {
	di(Router);

	const { themeColor, titleColor } = props;
	const readableTextColorFromTheme = readableTextColor(themeColor);
	const lightColorFromTheme = lightenColor(themeColor);
	const routes = useMemo(() => getRoutes(), []);
	const location = globalThis.location.pathname + globalThis.location.search;
	const theme = {
		titleColor,
		readableTextColor: readableTextColorFromTheme,
		color: themeColor,
		lightColor: lightColorFromTheme,
	};

	if (__SERVER__) {
		return (
			<ThemeProvider theme={theme}>
				<Router
					routes={routes}
					isGlobal={false}
					basePath={getBaseName()}
					history={createMemoryHistory({ initialEntries: [location] })}
					plugins={[resourcesPlugin]}
				>
					<GlobalStyled />
					<RouteComponent />
				</Router>
			</ThemeProvider>
		);
	}

	//isGlobal=false, to avoid the router conflicting with the sidebar's router.
	return (
		<ThemeProvider theme={theme}>
			<Router
				routes={routes}
				history={history as BrowserHistory}
				isGlobal={false}
				plugins={[resourcesPlugin]}
			>
				<GlobalStyled />
				{!__SERVER__ ? <UFOWatcher /> : null}
				<RouteComponent />
				{!__SERVER__ && FeatureGates.checkGate('enable_rovo_in_help_hubs') && (
					<RovoWidgetRenderer />
				)}
			</Router>
		</ThemeProvider>
	);
};

const selector = createSelector(
	getThemeColor,
	getHelpCenterTitleColor,
	(themeColor, titleColor) => ({
		themeColor,
		titleColor,
	}),
);

export default connect(selector)(HubResourceRouter);
